import {getIfNotEmpty, notEmpty} from '@megaphonenpm/mputils/src/utils.js';
import {get} from 'lodash-es';
import {SceneService} from './SceneService.js';
import {PRODUCTNAME_TWEETTOTV, PRODUCTNAME_UGC} from './constants.js';

/**
 * Utilities for working with extensions on scenes and playlists defined at the Product or Project
 * level. Note that extensions are represented as "mixins" in the Megaphone Object Model.
 */
export class ExtensionService {

  /**
   * Returns the value of the extension specified by extensionName defined on the object specified
   * by obj.
   *
   * Example:
   * getExtension(MegaScreen.globals.appState.effectiveScene, 'layout');
   *
   *
   * @param obj An object, such as a scene or a playlist, that defines extensions.
   * @param extensionName The name of the extension, such as "episode_description".
   * @returns The value of the extension specified by extensionName, or null if not found.
   */
  getExtension (obj, extensionName) {
    let mixins = get(obj, 'mixins');
    if (notEmpty(mixins)) {
      let extension = mixins.find(elem => elem.field_name === extensionName)
      return getIfNotEmpty(extension, 'text');
    } else {
      return null;
    }
  }

  /**
   * Returns the value of the layout extension on the first object where it is found in the list
   * of supplied objects. For example, for UGC scenes, the layout extension's value might
   * be 'a' or 'b'.
   *
   * @param objects An array of objects to search for the layout extension.
   * @param propertyNames An array of layout-extension property names. The layout extension name is
   *                      normally just "layout" for scenes, but prefixed with the product name for
   *                      playlists. E.g., "ugc_layout".
   *
   * @param defaultValue The value returned if the extension is not found on any of the supplied objects.
   * @returns String The lowercase string value of the layout extension.
   */
  getLayout (objects, propertyNames = [], defaultValue = null) {
    let layout = defaultValue;
    for (let i = 0; i < objects.length; i++) {
      layout = this.getExtension(objects[i], propertyNames[i]);
      if (notEmpty(layout)) {
        return layout.toLowerCase();
      }
    }

    // The "layout" extension was not found, so return the default value
    return layout;
  }

  cascadingGetSceneLayout (scene, playlist) {
    let sceneService = new SceneService();
    let screenProductName = sceneService.getScreenProductName(scene);

    // The 'layout' extension applies to UGC and Tweet to TV scenes only, so return the value of
    // 'layout' for scenes of those products only. This condition avoids risking 'layout'-specific
    // code execution during other scenes, which could have unintended side effects such as the
    // sceneMediaUnderlay appearing during, say, a PicPick scene.
    if (screenProductName === PRODUCTNAME_UGC
        || screenProductName === PRODUCTNAME_TWEETTOTV) {
      return this.getLayout([scene, playlist], ['layout', screenProductName + '_layout']);
    } else {
      return null;
    }
  }
}
